import { getColumns, getDrawerFilters } from './helpers'
import { useMemo, useState } from 'react'
import { toTableRowItems } from './serializers'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Grid } from '@mui/material'
import s from '../../../commonStyles.modules.scss'
import { useQuery } from 'react-query'
import { formatAndDownloadResponse, formatPayloadDate } from 'utils/helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { makeCoreGetFileRequest, makeCoreGetRequest } from 'utils/api'
import { Table } from 'components/core-ui/Table'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { Filter } from 'components/core-ui/Table/TableDefinitions'
import dayjs from 'dayjs'
import { getScreenPermissions, getShopOptions } from '../../api'
import { SCREENS } from '../../constants'

const MetiFiles = ({ isHistorical }) => {
  const [tableItems, setTableItems] = useState<MetiFile[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const ticketExternalId = searchParams.get('ticketExternalId')
  const [liveTableFilters, setLiveTableFilters] = useState<Filter[]>([])

  const { data: permissions } = getScreenPermissions(showNotification, SCREENS.RECEPTION_METI)

  const downloadSentFile = async id => {
    try {
      const response = await makeCoreGetFileRequest(
        isHistorical
          ? `/shop-reception/metiIntegrationFileArchive/export/${id}?type=sentFile`
          : `/shop-reception/metiIntegrationFile/export/${id}?type=sentFile`,
        {
          responseType: 'blob'
        }
      )
      return formatAndDownloadResponse(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const downloadSentFileHistory = async id => {
    try {
      const response = await makeCoreGetFileRequest(
        isHistorical
          ? `/shop-reception/metiIntegrationFileArchive/history/${id}?type=sentFile`
          : `/shop-reception/metiIntegrationFile/history/${id}?type=sentFile`,
        {
          responseType: 'blob'
        }
      )
      return formatAndDownloadResponse(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const downloadReceivedFile = async id => {
    try {
      const response = await makeCoreGetFileRequest(
        isHistorical
          ? `/shop-reception/metiIntegrationFileArchive/export/${id}?type=receivedFile`
          : `/shop-reception/metiIntegrationFile/export/${id}?type=receivedFile`,
        {
          responseType: 'blob'
        }
      )
      return formatAndDownloadResponse(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const downloadReceivedFileHistory = async id => {
    try {
      const response = await makeCoreGetFileRequest(
        isHistorical
          ? `/shop-reception/metiIntegrationFileArchive/history/${id}?type=receivedFile`
          : `/shop-reception/metiIntegrationFile/history/${id}?type=receivedFile`,
        {
          responseType: 'blob'
        }
      )
      return formatAndDownloadResponse(response)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const columns = useMemo(
    () =>
      getColumns(
        downloadSentFile,
        downloadSentFileHistory,
        downloadReceivedFile,
        downloadReceivedFileHistory,
        navigate,
        isHistorical,
        permissions?.canView
      ),
    [permissions?.canView, isHistorical]
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)

    const filters = tableFilters.map(filter =>
      filter.name === 'sentFileDate'
        ? { name: filter.name, value: formatPayloadDate(filter.value) }
        : filter.name === 'receivedFileDate'
        ? { name: filter.name, value: formatPayloadDate(filter.value) }
        : filter.name === 'ticketExternalId' && ticketExternalId
        ? { name: filter.name, value: filter.value || ticketExternalId }
        : filter
    )
    ticketExternalId && searchParams.delete('ticketExternalId')
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: isHistorical
            ? '/shop-reception/metiIntegrationFileArchive'
            : '/shop-reception/metiIntegrationFile',
          tableFilters: filters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.metiIntegrationFileList))

      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
      setIsLoading(false)
    }
  }

  const { data: shopCodes } = getShopOptions(showNotification)

  const { data: statusList, isLoading: isStatusListLoading } = useQuery(
    ['status-list'],
    async () => {
      const data = await makeCoreGetRequest(
        '/shop-reception/metiIntegrationFile/metiFileStatusList'
      )
      return data.map(item => ({
        label: item,
        value: item
      }))
    },
    {
      onError: error => {
        showNotification({
          message: `A apărut o eroare la obtinerea statusurilor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const filters = useMemo(() => {
    let minReceivedFileDate = null
    let maxSentFileDate = null

    const sentFileDate = liveTableFilters.find(filter => filter.name === 'sentFileDate')
    if (sentFileDate && !Array.isArray(sentFileDate.value)) {
      minReceivedFileDate = dayjs(String(sentFileDate.value))
    }

    const receivedFileDate = liveTableFilters.find(filter => filter.name === 'receivedFileDate')
    if (receivedFileDate && !Array.isArray(receivedFileDate.value)) {
      maxSentFileDate = dayjs(String(receivedFileDate.value))
    }

    return getDrawerFilters(shopCodes, statusList, ticketExternalId, {
      minReceivedFileDate,
      maxSentFileDate
    })
  }, [shopCodes, statusList, ticketExternalId, liveTableFilters])

  return (
    <Grid className={s.mainContainer}>
      <Table
        hideCheckbox
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        filters={filters}
        handleFilterChangeLive={liveFilters => setLiveTableFilters(liveFilters)}
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Grid>
  )
}

export default MetiFiles
